import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../common/SafeIcon";
import { useAuth } from "../../lib/auth";
import { cn } from "../../lib/utils";
import toast from "react-hot-toast";

const {
  FiLogOut,
  FiHome,
  FiActivity,
  FiBrain,
  FiShield,
  FiZap,
  FiUsers,
  FiAlertTriangle,
  FiMenu,
  FiX,
  FiUserCheck
} = FiIcons;

const AppLayout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isDark = user?.role === "clinician" || user?.role === "admin";

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (
      user?.role === "patient" &&
      !user?.protocol_training_completed &&
      location.pathname !== "/safety"
    ) {
      toast.error("Safety Audit Required", {
        icon: <SafeIcon icon={FiAlertTriangle} />,
      });
      navigate("/patient");
    }
  }, [user, location.pathname, navigate]);

  const navConfigs = {
    patient: [
      { id: "summary", label: "Overview", icon: FiHome, path: "/patient" },
      { id: "neuro", label: "Neuro Health", icon: FiBrain, path: "/patient" },
      {
        id: "vitals",
        label: "Live Stream",
        icon: FiActivity,
        path: "/patient",
      },
      { id: "safety", label: "Emergency", icon: FiShield, path: "/patient" },
    ],
    clinician: [
      {
        id: "overview",
        label: "Clinical Overview",
        icon: FiHome,
        path: "/dashboard",
      },
      {
        id: "triage",
        label: "Triage Center",
        icon: FiActivity,
        path: "/dashboard/triage", // ← was "/dashboard"; now its own route
      },
      {
        id: "patients",
        label: "Patient Roster",
        icon: FiUsers,
        path: "/dashboard/roster",
      },
      {
      id: "demographics",                       // <-- new
      label: "Demographic Verification",
      icon: FiUserCheck,
      path: "/dashboard/demographics",
    },
    ],
    admin: [
      {
        id: "overview",
        label: "System Status",
        icon: FiActivity,
        path: "/admin",
      },
      {
        id: "security",
        label: "Security Audit",
        icon: FiShield,
        path: "/admin",
      },
    ],
  };

  const navItems = navConfigs[user?.role] || navConfigs.patient;

  const SidebarContent = () => (
    <>
      <div className="p-6 lg:p-8">
        <Link
          to="/"
          className={cn(
            "flex items-center space-x-3 mb-8 lg:mb-12",
            isDark ? "text-blue-400" : "text-brand-600",
          )}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className={cn(
              "w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg",
              isDark ? "bg-blue-600 text-white" : "bg-brand-500 text-white",
            )}
          >
            <SafeIcon icon={FiZap} className="text-xl" />
          </div>
          <span className="text-2xl font-black tracking-tighter">
            iVivaCore
          </span>
        </Link>

        <nav className="space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={cn(
                "flex items-center space-x-3 px-4 lg:px-6 py-3 lg:py-4 rounded-2xl text-sm font-bold transition-all",
                isDark
                  ? location.pathname === item.path
                    ? "bg-blue-600 text-white shadow-xl"
                    : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                  : location.pathname === item.path
                    ? "bg-gray-900 text-white shadow-xl"
                    : "text-gray-400 hover:bg-gray-50",
                user?.role === "patient" &&
                  !user?.protocol_training_completed &&
                  item.id !== "summary" &&
                  item.id !== "safety"
                  ? "opacity-30 pointer-events-none"
                  : "",
              )}
            >
              <SafeIcon icon={item.icon} className="text-lg flex-shrink-0" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
      </div>

      <div
        className={cn(
          "mt-auto p-6 lg:p-8 border-t",
          isDark ? "border-slate-800" : "border-gray-50",
        )}
      >
        <div className="flex items-center space-x-3 mb-4 lg:mb-6">
          <div
            className={cn(
              "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
              isDark ? "bg-slate-800" : "bg-brand-50",
            )}
          >
            <SafeIcon
              icon={FiUsers}
              className={isDark ? "text-blue-400" : "text-brand-600"}
            />
          </div>
          <div className="truncate">
            <p
              className={cn(
                "text-sm font-black truncate",
                isDark ? "text-slate-200" : "text-gray-900",
              )}
            >
              {user?.first_name}
            </p>
            <p
              className={cn(
                "text-[10px] font-bold uppercase tracking-widest",
                isDark ? "text-slate-500" : "text-gray-400",
              )}
            >
              {user?.role}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            signOut();
            setIsMobileMenuOpen(false);
          }}
          className={cn(
            "w-full flex items-center justify-center space-x-2 font-bold transition-colors py-3 rounded-xl text-sm",
            isDark
              ? "text-slate-500 hover:text-red-400 hover:bg-red-900/20"
              : "text-gray-400 hover:text-red-500 hover:bg-red-50",
          )}
        >
          <SafeIcon icon={FiLogOut} />
          <span>Sign Out</span>
        </button>
      </div>
    </>
  );

  return (
    <div
      className={cn(
        "min-h-screen flex flex-col lg:flex-row",
        isDark ? "bg-slate-950 text-slate-200" : "bg-[#FDFDFD] text-gray-900",
      )}
    >
      {/* ── Mobile Top Bar ── */}
      <header
        className={cn(
          "lg:hidden flex items-center justify-between px-4 py-3 border-b sticky top-0 z-50",
          isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100",
        )}
      >
        <Link
          to="/"
          className={cn(
            "flex items-center space-x-2",
            isDark ? "text-blue-400" : "text-brand-600",
          )}
        >
          <div
            className={cn(
              "w-8 h-8 rounded-xl flex items-center justify-center",
              isDark ? "bg-blue-600 text-white" : "bg-brand-500 text-white",
            )}
          >
            <SafeIcon icon={FiZap} className="text-base" />
          </div>
          <span className="text-lg font-black tracking-tighter">iVivaCore</span>
        </Link>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={cn(
            "p-2 rounded-xl transition-colors",
            isDark
              ? "text-slate-400 hover:bg-slate-800"
              : "text-gray-500 hover:bg-gray-100",
          )}
        >
          <SafeIcon
            icon={isMobileMenuOpen ? FiX : FiMenu}
            className="text-xl"
          />
        </button>
      </header>

      {/* ── Mobile Drawer ── */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Drawer */}
          <div
            className={cn(
              "lg:hidden fixed top-0 left-0 h-full w-72 z-50 flex flex-col shadow-2xl",
              isDark ? "bg-slate-900" : "bg-white",
            )}
          >
            <SidebarContent />
          </div>
        </>
      )}

      {/* ── Desktop Sidebar ── */}
      <aside
        className={cn(
          "hidden lg:flex w-72 flex-col sticky top-0 h-screen z-40 border-r",
          isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-100",
        )}
      >
        <SidebarContent />
      </aside>

      {/* ── Main Content ── */}
      <main className="flex-1 overflow-x-hidden min-h-screen">
        {user?.role === "patient" && !user?.protocol_training_completed && (
          <div className="m-4 p-4 lg:p-6 bg-red-50 border border-red-100 rounded-[2rem] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-red-500 text-white rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                <SafeIcon
                  icon={FiAlertTriangle}
                  className="text-lg lg:text-xl"
                />
              </div>
              <div>
                <h4 className="text-red-900 font-black text-sm lg:text-base">
                  Mandatory Safety Audit Overdue
                </h4>
                <p className="text-red-700 text-xs font-medium">
                  Verify your Protocol 4422 contacts to unlock full dashboard
                  access.
                </p>
              </div>
            </div>
            <button className="px-4 lg:px-6 py-2 lg:py-3 bg-red-900 text-white rounded-xl font-black text-[10px] uppercase tracking-widest flex-shrink-0">
              Start Audit
            </button>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;